import React from 'react'
import type { Block } from '../../editor/types'

interface Message {
  text: string
  sender: 'left' | 'right'
  name: string
  avatar?: string
}

interface ChatBubbleBlockProps {
  block: Block
  variableValues: Record<string, number | string | boolean>
}

function renderText(text: string, variableValues: Record<string, number | string | boolean>, accent: string) {
  return text.split(/(@\w+)/g).map((part, i) => {
    const match = part.match(/^@(\w+)$/)
    if (match) {
      const value = variableValues[match[1]]
      return (
        <span key={i} className="font-mono font-medium px-1 rounded" style={{ color: accent, backgroundColor: `color-mix(in srgb, ${accent} 10%, transparent)` }}>
          {value !== undefined ? String(value) : part}
        </span>
      )
    }
    return <span key={i}>{part}</span>
  })
}

type ChatTheme = 'default' | 'imessage' | 'whatsapp' | 'slack'

export default function ChatBubbleBlock({ block, variableValues }: ChatBubbleBlockProps) {
  const config = (block.config || {}) as Record<string, unknown>
  const messages = (config.messages as Message[]) || []
  const theme = (config.theme as ChatTheme) || 'default'
  const accent = block.accent || 'var(--accent, #5048e5)'

  if (messages.length === 0) {
    return <p className="text-sm text-slate-400">No messages defined</p>
  }

  if (theme === 'slack') {
    return (
      <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
        {messages.map((msg, i) => (
          <div key={i} className={`flex gap-2.5 px-4 py-2 hover:bg-slate-50 ${i > 0 ? 'border-t border-slate-100' : ''}`}>
            <div className="w-8 h-8 rounded-md shrink-0 flex items-center justify-center text-white text-xs font-bold mt-0.5" style={{ backgroundColor: msg.sender === 'right' ? accent : '#94a3b8' }}>
              {msg.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="flex items-baseline gap-2">
                <span className="text-sm font-bold text-slate-900">{msg.name}</span>
                <span className="text-[10px] text-slate-400">12:{String(i).padStart(2, '0')} PM</span>
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">{renderText(msg.text, variableValues, accent)}</p>
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (theme === 'whatsapp') {
    return (
      <div className="rounded-xl p-3 space-y-1.5" style={{ backgroundColor: '#e5ddd5', backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'200\' height=\'200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cdefs%3E%3Cpattern id=\'a\' width=\'40\' height=\'40\' patternUnits=\'userSpaceOnUse\'%3E%3Cpath d=\'M0 20h40M20 0v40\' fill=\'none\' stroke=\'%23d4ccb5\' stroke-width=\'.3\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width=\'200\' height=\'200\' fill=\'url(%23a)\'/%3E%3C/svg%3E")' }}>
        {messages.map((msg, i) => {
          const isRight = msg.sender === 'right'
          return (
            <div key={i} className={`flex ${isRight ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[75%] px-3 py-1.5 rounded-lg text-sm leading-relaxed shadow-sm ${
                  isRight ? 'rounded-tr-none' : 'rounded-tl-none'
                }`}
                style={{ backgroundColor: isRight ? '#dcf8c6' : 'white' }}
              >
                {!isRight && <span className="text-xs font-semibold text-emerald-600 block">{msg.name}</span>}
                <span className="text-slate-800">{renderText(msg.text, variableValues, accent)}</span>
                <span className="text-[9px] text-slate-400 ml-2 float-right mt-1">12:{String(i).padStart(2, '0')}</span>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  if (theme === 'imessage') {
    return (
      <div className="space-y-1.5 py-2">
        {messages.map((msg, i) => {
          const isRight = msg.sender === 'right'
          return (
            <div key={i} className={`flex ${isRight ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[75%] px-4 py-2 rounded-[18px] text-sm leading-relaxed ${
                  isRight ? 'text-white rounded-br-sm' : 'rounded-bl-sm'
                }`}
                style={{
                  backgroundColor: isRight ? '#007AFF' : '#e9e9eb',
                  color: isRight ? 'white' : '#1c1c1e',
                }}
              >
                {renderText(msg.text, variableValues, accent)}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // Default theme
  return (
    <div className="space-y-3">
      {messages.map((msg, i) => {
        const isRight = msg.sender === 'right'
        return (
          <div key={i} className={`flex ${isRight ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[75%] ${isRight ? 'items-end' : 'items-start'} flex flex-col`}>
              <span className="text-[10px] font-medium text-slate-400 mb-1 px-3">{msg.name}</span>
              <div
                className={`relative px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  isRight
                    ? 'text-white rounded-br-md'
                    : 'bg-slate-100 text-slate-800 rounded-bl-md'
                }`}
                style={isRight ? { backgroundColor: accent } : undefined}
              >
                {renderText(msg.text, variableValues, accent)}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
